import { getRegionCurrencySymbol } from 'common/config/regions/utils';
import { alphanumericRegex, companyNameRegex, latinLettersNumericRegex } from 'common/constants/regexes';
import { dayjs } from 'common/dayjs';
import { AntFormRule } from 'components/ant/AntForm/AntForm.types';
import type { Dayjs } from 'dayjs';
import { AddressProviderType } from 'graphql/queries/types';

const NA = '–';
const DEFAULT_MONEY_VALUE = '0';

export const DATE_F = 'DD.MM.YYYY';
export const DATETIME_F = 'DD.MM.YYYY HH:mm';
export const DATE_TIME_FORMAT = 'HH:mm DD.MM.YYYY';

export const valueOrNa = (value: any): any => (value == null || value === '' ? NA : value);

export const currency = (value: any, currency = getRegionCurrencySymbol()): string => {
  const parsedValue = parseFloat(String(value).replace(',', '.'));

  if (value == null || isNaN(parsedValue)) return `${currency} ${DEFAULT_MONEY_VALUE}`;

  const withSpaces = parsedValue.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

  return `${currency} ${withSpaces}`;
};

export const percent = (value: any): string => {
  const parsedValue = parseFloat(String(value).replace(',', '.'));

  if (value == null || isNaN(parsedValue)) return NA;

  return `${Math.round(parsedValue * 10) / 10}%`;
};

export const mm2cm = (value: number): number => value / 1000;

const DAY_FIRST_FORMATS = ['DD-MM-YYYY HH:mm', 'DD-MM-YYYY'];
const DAY_FIRST_RE = /^\d{2}-\d{2}-\d{4}/;

export const parseDate = (date: string | Dayjs): Dayjs => {
  const value = typeof date === 'string' ? date : date.toISOString();

  /**
   * A day-first string needs the explicit formats — dayjs's ISO parser reads `31-01-2026` as a
   * year. Everything else goes to the native parser, the only one that keeps a UTC offset:
   * the `Z` token of `customParseFormat` matches a literal `Z`, so a strict format list would
   * shift every backend timestamp by the local offset.
   */
  return DAY_FIRST_RE.test(value) ? dayjs(value, DAY_FIRST_FORMATS, true) : dayjs(value);
};

export const dateFormat = (date?: string | Dayjs | null, format = DATE_F) => {
  if (!date) return NA;

  return parseDate(date).format(format);
};

export const dateFormatToBackend = (date: Dayjs): string => date.format(DATE_F);

export const dateDecoratedFormat = (date: string) => {
  if (!date) return NA;

  const value = dayjs(date);
  const diff = dayjs().diff(value, 'days');

  switch (diff) {
    case 0:
      return `Today ${value.format('HH:mm')}`;
    case 1:
      return `Yesterday ${value.format('HH:mm')}`;
    default:
      return value.format(DATE_F);
  }
};

export const onlyLatinRule = {
  pattern: latinLettersNumericRegex,
  message: 'Only latin letters and digits allowed',
};

export const companyNameRule = {
  pattern: companyNameRegex,
  message: "Only latin letters, digits, spaces and . , - & ' are allowed",
};

export const alphanumericOnly = (): AntFormRule => ({
  pattern: alphanumericRegex,
  message: 'Only alphanumeric characters are allowed',
});

export const dateHoursDiffFormat = (rawStart: string | Dayjs, rawEnd: string | Dayjs) => {
  if (!rawStart || !rawEnd) return NaN;

  const start = parseDate(rawStart);
  const end = parseDate(rawEnd);

  const diff = dayjs.duration(end.diff(start)).asHours();

  if (diff < 0) {
    return 0;
  }

  return diff;
};

export const dateDaysDiffFormat = (rawStart: string | Dayjs, rawEnd: string | Dayjs) => {
  if (!rawStart || !rawEnd) return NaN;

  const start = parseDate(rawStart);
  const end = parseDate(rawEnd);

  const diff = dayjs.duration(end.diff(start)).asDays();

  if (diff < 0) {
    return 0;
  }

  return diff;
};

export const dateTimeFormat = (date: string | Dayjs) => {
  if (!date) return NA;

  return parseDate(date).format('DD.MM.YYYY HH:mm');
};

export const shortName = (longName: string | null | undefined, length: number) => {
  if (!longName || longName.length <= length) {
    return longName;
  }

  const prefixLength = Math.round(length / 2);
  const tailLength = prefixLength;

  return `${longName.substr(0, prefixLength - 1)}…${longName.substr(longName.length - tailLength + 1, tailLength)}`;
};

export const address = (data: AddressProviderType | undefined) => {
  if (!data) {
    return '-';
  }

  return [data.country, data.region, data.city, data.address, data.zip].filter(Boolean).join(', ');
};

export const pluralize = (one: string, many: string, count: number): string => (count === 1 ? one : many);

export const toPhoneLink = (phone: string) =>
  `tel:${phone}`
    .replace(/\s/g, '')
    .replace(/-/g, '')
    .replace(/\(?\)?/g, '');

export const toMailLink = (mail: string) => `mailto:${mail}`;

export const numberParser = (value: string | undefined): string | number => {
  if (value === undefined || value === null) return '0';

  let stringValue = String(value);

  if (stringValue.includes(',') && !stringValue.includes('.')) {
    stringValue = stringValue.replace(/,/g, '.');
  }

  return `${stringValue}`.replace(/[^0-9.-]/g, '');
};
